import React, { useState } from 'react';
import { cleanNonAsci } from 'utils';
import { useLocale } from 'locale';
import { Text, HrefText, SmallText } from 'ds/components/typography';
import { DescriptionBody, DescriptionReadMore } from './styled';
import trimHtml from 'utils/html';

export type Size = 'small' | 'default';
const LIMIT = 150;

export interface DescriptionProps {
  description: string;
  limit?: number;
  clean?: boolean;
  size?: Size;
  onToggle?: (isDescriptionOpened: boolean) => void;
  showOnClick?: boolean;
  whiteSpaceNormal?: boolean;
  expandable?: boolean;
}

export interface DescriptionState {
  isDescriptionOpened: boolean;
}

export const Description: React.FC<DescriptionProps> = (props) => {
  const {
    size = 'default',
    limit = LIMIT,
    description,
    clean,
    onToggle,
    showOnClick,
    whiteSpaceNormal,
    expandable = true,
  } = props;
  const [ isOpened, setIsOpened ] = useState(false);
  const { t } = useLocale();

  const formatDescription = (str: string) => {
    if (!str) return null;

    const cleanDescription = clean ? cleanNonAsci(description) : str;
    const trimPreview = trimHtml(cleanDescription, { limit, wordBreak: true });

    return {
      html: cleanDescription,
      preview: trimPreview.html,
      more: trimPreview.more,
    };
  };

  const toggleDescription = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsOpened(!isOpened);

    if (onToggle) {
      onToggle(!isOpened);
    }
  };

  const formattedDescription = formatDescription(description);

  const Wrapper = size === 'small' ? SmallText : Text;
  return formattedDescription ? (
    <Wrapper onClick={showOnClick && !isOpened ? toggleDescription : undefined}>
      <DescriptionBody>
        {isOpened ? (
          <span style={whiteSpaceNormal ? { whiteSpace: 'normal' } : {}} dangerouslySetInnerHTML={{ __html: formattedDescription.html }} />
        ) : (
          <span style={whiteSpaceNormal ? { whiteSpace: 'normal' } : {}} dangerouslySetInnerHTML={{ __html: formattedDescription.preview }} />
        )}
        {formattedDescription.more && expandable ? (
          <DescriptionReadMore
            data-auto={`read-${isOpened ? 'less' : 'more'}-button`}
            onClick={toggleDescription}
          >
            <HrefText className="description-read-more">
              <Wrapper>
                {t('description.toggle', { isOpened })}
              </Wrapper>
            </HrefText>
          </DescriptionReadMore>
        ) : null}
      </DescriptionBody>
    </Wrapper>
  ) : null;
};


export default Description;
